import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  signal,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, takeUntil } from 'rxjs';
import { LiquidationService } from '../liquidation.service';
import { LiquidationAttributes, LiquidationDetail } from '../interfaces/liquidation.interface';
import { IPagination } from 'app/common/interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';
import { SortDirectionEnum } from 'app/common/enums/sort-direction.enum';

// Smart Grid Imports
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';

// Cell Renderer Imports
import { LiquidationDateCellRendererComponent } from './cell-renderers/liquidation-date-cell-renderer.component';
import { LiquidationUserCellRendererComponent } from './cell-renderers/liquidation-user-cell-renderer.component';
import { LiquidationPriceListCellRendererComponent } from './cell-renderers/liquidation-pricelist-cell-renderer.component';
import { LiquidationStatusCellRendererComponent } from './cell-renderers/liquidation-status-cell-renderer.component';
import { MaterialCodeNameCellRendererComponent } from './cell-renderers/material-codename-cell-renderer.component';
import { LiquidationQuantityUMPCellRendererComponent } from './cell-renderers/liquidation-quantity-ump-cell-renderer.component';
import { LiquidationDocumentCellRendererComponent } from './cell-renderers/liquidation-document-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';

@Component({
  selector: 'app-liquidations',
  standalone: true,
  imports: [
    CommonModule,
    SmartGridComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    UnitMeasureDisplayComponent,
  ],
  templateUrl: './liquidations.component.html',
  styleUrl: './liquidations.component.scss',
})
export class LiquidationsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() tripId: number | null = null;
  @Input() UMPActive$!: Observable<boolean>;
  @ViewChild(SmartGridComponent) grid!: SmartGridComponent<any>;

  private destroy$ = new Subject<void>();

  // Signals para manejo de estado
  isLoading = signal<boolean>(false);
  liquidations = signal<LiquidationAttributes[]>([]);
  totalItems = signal<number>(0);

  // Smart Grid Column Definitions
  liquidationsColumnDefs: ColDef<any>[] = [];
  detailColumnDefs: ColDef<any>[] = [];

  // Smart Grid Configurations
  liquidationsGridConfig: GridConfig<any> = {};
  detailGridConfig: GridConfig<any> = {};

  // Opciones de paginación (mantenidas para service calls)
  currentPage = 1;
  pageSize = 10;

  constructor(private liquidationService: LiquidationService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.initializeLiquidationsColumnDefs();
    this.initializeDetailColumnDefs();
    this.initializeLiquidationsGridConfig();
    this.initializeDetailGridConfig();

    if (this.tripId) {
      this.loadLiquidations();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tripId']?.currentValue !== changes['tripId']?.previousValue) {
      this.loadLiquidations();
    }
    // UMPActive$ Observable changes are handled automatically by async pipe in template
  }

  /**
   * Carga las liquidaciones para el tripId actual
   */
  private loadLiquidations(): void {
    if (!this.tripId) {
      return;
    }

    this.isLoading.set(true);

    const options: IPagination<LiquidationAttributes> = {
      currentPage: this.currentPage,
      limitPerPage: this.pageSize,
      sort: 'createdAt',
      sortDirection: SortDirectionEnum.DESC,
    };

    this.liquidationService
      .getLiquidationByTripId(this.tripId, options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const liquidationsData = response.data?.[0]?.data || [];
          const total = response.data?.[0]?.pageInfo?.[0]?.total || 0;

          this.liquidations.set(liquidationsData);
          this.totalItems.set(total);
          this.isLoading.set(false);

          // Auto-expand first row after grid initializes
          this.autoExpandFirstRow();
        },
        error: error => {
          this.snackBar.open(
            error.error.message || "Error al obtener liquidaciones.",
            "Cerrar",
            { duration: 5000 }
          );
          this.isLoading.set(false);
          this.liquidations.set([]);
        },
      });
  }

  /**
   * Auto-expande la primera fila después de cargar los datos
   */
  private autoExpandFirstRow(): void {
    if (this.liquidations().length > 0) {
      // Wait for grid to initialize
      setTimeout(() => {
        const firstRow = this.liquidations()[0];
        this.grid?.expandRow(firstRow);
      }, 100);
    }
  }

  /**
   * Maneja el cambio de página en el paginador
   * NOTE: Smart-grid handles pagination internally, but we keep this for service calls if needed
   */
  onPageChange(pageIndex: number, pageSize: number): void {
    this.currentPage = pageIndex + 1;
    this.pageSize = pageSize;
    this.loadLiquidations();
  }

  /**
   * Recargar las liquidaciones
   */
  onRefresh(): void {
    this.currentPage = 1;
    this.loadLiquidations();
  }

  // ============================================
  // SMART-GRID INITIALIZATION METHODS
  // ============================================

  private initializeLiquidationsColumnDefs(): void {
    this.liquidationsColumnDefs = [
      // 1. DOCUMENT SERIAL
      {
        colId: 'serial',
        field: 'serial' as any,
        headerName: 'No. Documento',
        width: 180,
        minWidth: 150,
        sortable: true,
        filterable: true,
        pinned: 'left',
        cellRenderer: LiquidationDocumentCellRendererComponent,
        valueGetter: (data) => data.serial,
        responsive: {
          hideOnMobile: false,
          responsivePriority: 1
        }
      },

      // 2. CREATION DATE
      {
        colId: 'createdAt',
        field: 'createdAt' as any,
        headerName: 'Fecha',
        width: 180,
        minWidth: 160,
        sortable: true,
        filterable: true,
        cellRenderer: LiquidationDateCellRendererComponent,
        valueGetter: (data) => data.createdAt,
        filterOptions: {
          valueExtractor: (data) => data.createdAt,
          displayNameGetter: (value) => {
            if (!value) return '-';
            const date = new Date(value);
            return date.toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        },
        responsive: {
          hideOnMobile: false,
          responsivePriority: 2
        }
      },

      // 3. USER
      {
        colId: 'user',
        field: 'user' as any,
        headerName: 'Usuario',
        width: 220,
        minWidth: 180,
        sortable: true,
        filterable: true,
        cellRenderer: LiquidationUserCellRendererComponent,
        valueGetter: (data) => data.user?.user_login?.username,
        filterOptions: {
          valueExtractor: (data) => data.user?.user_login?.username,
          displayNameGetter: (value) => value || '(Sin usuario)'
        },
        responsive: {
          hideOnTablet: true,
          responsivePriority: 5
        }
      },

      // 4. PRICE LIST
      {
        colId: 'priceList',
        field: 'priceList' as any,
        headerName: 'Lista Precios',
        width: 200,
        minWidth: 180,
        sortable: true,
        filterable: true,
        cellRenderer: LiquidationPriceListCellRendererComponent,
        valueGetter: (data) => data.priceList?.name,
        filterOptions: {
          valueExtractor: (data) => data.priceList?.name,
          displayNameGetter: (value) => value || '(Sin lista de precios)'
        },
        responsive: {
          hideOnTablet: true,
          responsivePriority: 6
        }
      },

      // 5. TOTAL GOOD CONDITION (BE)
      {
        colId: 'totalBE',
        field: 'totalToPayForReturnedBE.total' as any,
        headerName: 'Total Buen Estado',
        width: 150,
        minWidth: 130,
        sortable: true,
        filterable: false,
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'text-right',
        headerClass: 'text-right',
        valueGetter: (data) => data.totalToPayForReturnedBE?.total ?? 0,
        responsive: {
          hideOnMobile: true,
          responsivePriority: 7
        }
      },

      // 6. TOTAL BAD CONDITION (ME)
      {
        colId: 'totalME',
        field: 'totalToPayForReturnedME.total' as any,
        headerName: 'Total Mal Estado',
        width: 150,
        minWidth: 130,
        sortable: true,
        filterable: false,
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'text-right',
        headerClass: 'text-right',
        valueGetter: (data) => data.totalToPayForReturnedME?.total ?? 0,
        responsive: {
          hideOnMobile: true,
          responsivePriority: 8
        }
      },

      // 7. TOTAL MISSING
      {
        colId: 'totalMissing',
        field: 'totalToPayForMissing.total' as any,
        headerName: 'Faltante',
        width: 130,
        minWidth: 110,
        sortable: true,
        filterable: false,
        cellRenderer: DecimalCellRendererComponent,
        cellRendererParams: {
          decimals: 2,
          showCurrency: false
        },
        cellClass: 'text-right',
        headerClass: 'text-right',
        valueGetter: (data) => data.totalToPayForMissing?.total ?? 0,
        responsive: {
          hideOnMobile: true,
          responsivePriority: 9
        }
      },

      // 8. STATUS
      {
        colId: 'status',
        field: 'statusERP' as any,
        headerName: 'Estado',
        width: 180,
        minWidth: 150,
        sortable: true,
        filterable: true,
        cellRenderer: LiquidationStatusCellRendererComponent,
        cellClass: 'text-center',
        headerClass: 'text-center',
        valueGetter: (data) => data.statusERP,
        responsive: {
          hideOnMobile: false,
          responsivePriority: 3
        }
      }
    ];
  }

  private initializeDetailColumnDefs(): void {
    this.detailColumnDefs = [
      // 1. POSITION
      {
        colId: 'position',
        field: 'position' as any,
        headerName: 'Pos.',
        width: 70,
        minWidth: 60,
        sortable: false,
        filterable: false,
        cellClass: 'text-center font-semibold',
        headerClass: 'text-center'
      },

      // 2. MATERIAL
      {
        colId: 'material',
        field: 'material' as any,
        headerName: 'Material',
        width: 250,
        minWidth: 200,
        sortable: false,
        filterable: false,
        cellRenderer: MaterialCodeNameCellRendererComponent,
        valueGetter: (data) => data.material?.name
      },

      // 3. LOADED QUANTITY
      {
        colId: 'loaded',
        headerName: 'Cargado',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: LiquidationQuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
          quantityField: 'totalLoaded',
          baseUOMField: 'baseUnitOfMeasure',
          parallelUOMField: 'pararellUnitOfMeasure'
        }
      },

      // 4. DELIVERED QUANTITY
      {
        colId: 'delivered',
        headerName: 'Entregado/Facturado',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: LiquidationQuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
          quantityField: 'totalDelivered',
          baseUOMField: 'baseUnitOfMeasure',
          parallelUOMField: 'pararellUnitOfMeasure'
        }
      },

      // 5. RETURNED GOOD CONDITION
      {
        colId: 'returnedBE',
        headerName: 'Buen Estado',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: LiquidationQuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
          quantityField: 'totalReturnedBE',
          baseUOMField: 'baseUnitOfMeasure',
          parallelUOMField: 'pararellUnitOfMeasure'
        }
      },

      // 6. RETURNED BAD CONDITION
      {
        colId: 'returnedME',
        headerName: 'Mal Estado',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: LiquidationQuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
          quantityField: 'totalReturnedME',
          baseUOMField: 'baseUnitOfMeasure',
          parallelUOMField: 'pararellUnitOfMeasure'
        }
      },

      // 7. MISSING QUANTITY
      {
        colId: 'missing',
        headerName: 'Faltante',
        width: 220,
        minWidth: 180,
        sortable: false,
        filterable: false,
        cellRenderer: LiquidationQuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
          quantityField: 'totalMissing',
          baseUOMField: 'baseUnitOfMeasure',
          parallelUOMField: 'pararellUnitOfMeasure'
        }
      }
    ];
  }

  private initializeLiquidationsGridConfig(): void {
    this.liquidationsGridConfig = {
      // DATA SOURCE
      dataMode: 'local',
      rowIdField: '_id' as any,

      // SORTING
      sorting: {
        enabled: true,
        mode: 'local',
        multiColumn: false,
        defaultSort: [
          { field: 'createdAt' as any, direction: 'desc' }
        ]
      },

      // PAGINATION
      pagination: {
        enabled: true,
        mode: 'local',
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
        showPaginator: true,
        showFirstLastButtons: true,
        position: 'bottom'
      },

      // SEARCH
      search: {
        enabled: false,
        mode: 'local',
        showSearchInput: false
      },

      // COLUMN FILTERS
      columnFilters: {
        enabled: true,
        mode: 'local',
        showFilterIcon: true,
        trigger: 'click'
      },

      // SELECTION
      selection: {
        enabled: false
      },

      // ROW EXPANSION (KEY FEATURE FOR NESTED DETAIL)
      rowExpansion: {
        enabled: true,
        expandOnClick: false,      // Only expand via button
        multipleExpand: false,      // Single expansion at a time
        expandColumn: {
          pinned: 'left',
          width: 50
        },
        animationDuration: 225
      },

      // SETTINGS PANEL
      settingsPanel: {
        enabled: true,
        position: 'right',
        enableColumnVisibility: true,
        enableDensity: true,
        enableExport: false,
        defaultDensity: 'comfortable'
      },

      // COLUMN PINNING
      columnPinning: {
        enabled: true,
        allowUserPinning: false
      },

      // STYLING
      styling: {
        density: 'comfortable',
        maxHeight: '70vh',
        headerHeight: 56,
        showBorders: true,
        stripedRows: false,
        rowHover: true,
        cellPaddingY: '8px'
      },

      // LOADING
      loading: {
        showOverlay: true,
        loadingText: 'Cargando liquidaciones...'
      },

      // EMPTY STATE
      emptyState: {
        message: 'No hay liquidaciones disponibles para este viaje.',
        showIcon: true,
        icon: 'description'
      },

      // STATE PERSISTENCE
      statePersistence: {
        enabled: true,
        storageKey: 'liquidations-grid',
        storageType: 'localStorage',
        features: {
          columnVisibility: true,
          columnPinning: true,
          sorting: false,
          filters: false,
          pagination: false
        }
      },

      // RESPONSIVE
      responsive: {
        enabled: true,
        breakpoints: {
          mobile: 640,
          tablet: 1024
        },
        mobileMode: 'compact',
        autoHideColumns: true
      }
    };
  }

  private initializeDetailGridConfig(): void {
    this.detailGridConfig = {
      // DATA SOURCE
      dataMode: 'local',
      rowIdField: '_id' as any,

      // DISABLE MOST FEATURES (simple display)
      pagination: { enabled: false },
      sorting: { enabled: false },
      search: { enabled: false },
      columnFilters: { enabled: false },
      selection: { enabled: false },
      settingsPanel: { enabled: false },
      rowExpansion: { enabled: false },

      // STYLING
      styling: {
        density: 'compact',
        maxHeight: '400px',
        headerHeight: 48,
        showBorders: true,
        stripedRows: true,
        rowHover: false,
        cellPaddingY: '4px'
      },

      // EMPTY STATE
      emptyState: {
        message: 'No hay materiales en esta liquidación.',
        showIcon: false
      }
    };
  }
}
